import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, errorMessage } from '../api/client';
import { diagnosticsApi, newEventId, workstationsApi } from '../api/endpoints';
import type {
  DuplicateDetails,
  ExtractionResult,
  InventoryProposal,
  Staging,
} from '../api/types';
import { Badge } from '../components/Badge';
import { Field } from '../components/Field';
import { Notice } from '../components/Notice';
import {
  ACCEPTED_UPLOAD_ACCEPT,
  DIAGNOSTIC_TYPES,
  diagnosticTypeLabel,
  fileFormatLabel,
  type DiagnosticType,
} from '../domain/diagnostics';
import { proposalRows, retainedLines } from '../domain/inventoryProposal';
import { PART_CATEGORY_LABELS } from '../domain/parts';
import { useAsync } from '../hooks/useAsync';
import { formatDateTime, formatSize, fromDateTimeInput, nowForInput } from '../lib/format';

type Step =
  | { kind: 'choix' }
  | {
      kind: 'confirmation';
      staging: Staging;
      extraction: ExtractionResult;
      proposal: InventoryProposal | null;
    };

type ConfirmForm = {
  type: DiagnosticType;
  performedDate: string;
  performedTime: string;
  reportUrl: string;
  reason: string;
  processeur: string;
  carteGraphique: string;
  memoire: string;
  disques: string;
};

function duplicateOf(error: unknown): NonNullable<DuplicateDetails['doublon']> | null {
  if (!(error instanceof ApiError)) return null;
  const details = error.details as DuplicateDetails | undefined;
  return details?.doublon ?? null;
}

function extractedValues(extraction: ExtractionResult): ExtractionResult['values'] {
  return extraction.status === 'ok' ? extraction.values : {};
}

function hourMissing(extraction: ExtractionResult): boolean {
  const performedAt = extractedValues(extraction).performedAt;
  return performedAt !== undefined && !performedAt.includes('T');
}

function splitWallDateTime(value: string): Pick<ConfirmForm, 'performedDate' | 'performedTime'> {
  const [performedDate = '', performedTime = ''] = value.split('T');
  return { performedDate, performedTime };
}

function prefill(extraction: ExtractionResult): ConfirmForm {
  const values = extractedValues(extraction);
  return {
    type: values.diagnosticType ?? 'diagnosticRapide',
    // la date extraite est une heure murale : elle alimente le champ tel quel
    ...splitWallDateTime(values.performedAt ?? nowForInput()),
    reportUrl: '',
    reason: '',
    processeur: values.processeur ?? '',
    carteGraphique: values.carteGraphique ?? '',
    memoire: values.memoire ?? '',
    disques: (values.disques ?? []).join('\n'),
  };
}

function ExtractionNotice({ extraction }: { extraction: ExtractionResult }) {
  if (extraction.status === 'ok') {
    return (
      <Notice tone="info" title="Extraction aboutie">
        Valeurs proposées par lecture automatique du rapport. Vérifiez-les, ce sont
        celles que vous confirmez qui feront foi.
      </Notice>
    );
  }
  return (
    <Notice
      tone="alerte"
      title={
        extraction.status === 'vide'
          ? "L'extraction n'a rien trouvé"
          : "L'extraction a échoué"
      }
    >
      {extraction.message ?? 'Aucune précision.'} Le fichier est bien reçu et sera
      conservé tel quel : saisissez les champs à la main ci-dessous.
    </Notice>
  );
}

function InventoryProposalPanel({
  proposal,
  checked,
  onToggle,
  onToggleAll,
  disabled,
}: {
  proposal: InventoryProposal;
  checked: ReadonlySet<number>;
  onToggle: (index: number) => void;
  onToggleAll: (indexes: number[]) => void;
  disabled: boolean;
}) {
  const rows = proposalRows(proposal);
  const checkable = rows.filter((row) => row.checkable).map((row) => row.index);
  const allChecked = checkable.length > 0 && checkable.every((index) => checked.has(index));

  return (
    <section className="panneau">
      <div className="panneau__entete">
        <span className="panneau__titre">Inventaire relevé dans le rapport</span>
        <span className="faible pousse">
          {retainedLines(rows, checked).length} ligne(s) retenue(s) sur {checkable.length}
        </span>
        {checkable.length > 0 && (
          <button
            type="button"
            className="bouton bouton--petit"
            disabled={disabled}
            onClick={() => onToggleAll(allChecked ? [] : checkable)}
          >
            {allChecked ? 'Tout décocher' : 'Tout cocher'}
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="panneau__corps">
          <p className="doux">Le modèle n’a relevé aucun composant dans ce rapport.</p>
        </div>
      ) : (
        <div className="panneau__corps panneau__corps--nu">
          <div className="tableau-cadre">
            <table className="tableau">
              <thead>
                <tr>
                  <th>Retenir</th>
                  <th>Composant</th>
                  <th>Marque</th>
                  <th>Modèle</th>
                  <th>Ligne source</th>
                  <th>Ce qui sera fait</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.index}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Retenir ${row.component.designation}`}
                        checked={row.checkable && checked.has(row.index)}
                        disabled={disabled || !row.checkable}
                        onChange={() => onToggle(row.index)}
                      />
                    </td>
                    <td>
                      <div className="tableau__ligne-titre">{row.component.designation}</div>
                      <div className="faible">
                        {PART_CATEGORY_LABELS[row.component.categorie]} · quantité{' '}
                        {row.component.quantite} · confiance{' '}
                        {Math.round(row.component.confiance * 100)} %
                      </div>
                    </td>
                    <td>{row.component.marque ?? '—'}</td>
                    <td>{row.component.modele ?? '—'}</td>
                    <td className="mono">{row.component.source}</td>
                    <td>
                      {row.raison === null ? (
                        row.action
                      ) : (
                        <>
                          <Badge tone="alerte">{row.action}</Badge>
                          <div className="doux">{row.raison}</div>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="panneau__corps">
        <p className="faible">
          Proposition calculée sur l’état du poste au {formatDateTime(proposal.calculatedAt)}.
          Elle est recalculée à l’enregistrement avec la date confirmée : une ligne
          devenue sans objet d’ici là est ignorée.
        </p>
      </div>
    </section>
  );
}

/**
 * Import d'un diagnostic sur un poste donné.
 *
 * Le poste vient du chemin, et non d'un sélecteur : on arrive ici depuis sa
 * fiche, où il est déjà choisi. Le rendre structurel plutôt qu'optionnel
 * supprime le cas « aucun poste » au lieu d'avoir à le traiter, et écarte du
 * même coup la création de poste à la volée — un poste se crée d'abord, depuis
 * la liste des postes, et l'import vient ensuite.
 */
export function ImportPage() {
  const navigate = useNavigate();
  const { id: workstationId = '' } = useParams();

  const [file, setFile] = useState<File | null>(null);
  const [step, setStep] = useState<Step>({ kind: 'choix' });
  const [form, setForm] = useState<ConfirmForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] =
    useState<NonNullable<DuplicateDetails['doublon']> | null>(null);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState<ReadonlySet<number>>(new Set());

  const workstation = useAsync(
    (signal) => workstationsApi.get(workstationId, signal),
    [workstationId],
  );

  const patch = (change: Partial<ConfirmForm>): void =>
    setForm((current) => (current === null ? current : { ...current, ...change }));

  const onUpload = (event: React.FormEvent): void => {
    event.preventDefault();
    if (file === null) return;
    setBusy(true);
    setError(null);
    setDuplicate(null);

    diagnosticsApi
      .stage(file, workstationId)
      .then((response) => {
        setStep({
          kind: 'confirmation',
          staging: response.staging,
          extraction: response.extraction,
          proposal: response.inventoryProposal,
        });
        setForm(prefill(response.extraction));
        setChecked(new Set());
      })
      .catch((cause: unknown) => {
        setDuplicate(duplicateOf(cause));
        setError(errorMessage(cause));
      })
      .finally(() => setBusy(false));
  };

  const onConfirm = (event: React.FormEvent): void => {
    event.preventDefault();
    if (step.kind !== 'confirmation' || form === null) return;
    setBusy(true);
    setError(null);
    setDuplicate(null);

    const confirmed: Record<string, string | string[]> = {};
    if (form.processeur !== '') confirmed.processeur = form.processeur;
    if (form.carteGraphique !== '') confirmed.carteGraphique = form.carteGraphique;
    if (form.memoire !== '') confirmed.memoire = form.memoire;
    const disks = form.disques
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line !== '');
    if (disks.length > 0) confirmed.disques = disks;

    diagnosticsApi
      .import({
        stagingId: step.staging.id,
        eventId: newEventId(),
        workstationId,
        type: form.type,
        performedAt: fromDateTimeInput(`${form.performedDate}T${form.performedTime}`),
        reportUrl: form.reportUrl === '' ? null : form.reportUrl,
        reason: form.reason,
        confirmed,
        retainedLines:
          step.proposal === null ? [] : retainedLines(proposalRows(step.proposal), checked),
      })
      .then(({ diagnostic }) => navigate(`/diagnostics/${diagnostic.id}`))
      .catch((cause: unknown) => {
        setDuplicate(duplicateOf(cause));
        setError(errorMessage(cause));
      })
      .finally(() => setBusy(false));
  };

  const onAbandon = (): void => {
    if (step.kind !== 'confirmation') return;
    const stagingId = step.staging.id;
    setBusy(true);
    diagnosticsApi
      .discardStaging(stagingId)
      .then(() => {
        setStep({ kind: 'choix' });
        setForm(null);
        setFile(null);
        setChecked(new Set());
        setError(null);
      })
      .catch((cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setBusy(false));
  };

  const poste = workstation.data?.workstation ?? null;

  if (workstation.error !== null) {
    return (
      <Notice tone="erreur" title="Poste introuvable">
        {workstation.error}
      </Notice>
    );
  }
  if (poste === null) {
    return <div className="chargement">Chargement du poste…</div>;
  }

  return (
    <div className="pile">
      <div className="entete-page">
        <div>
          <h1>Importer un diagnostic</h1>
          <p className="entete-page__sous-titre">
            Sur <Link to={`/postes/${poste.id}`}>{poste.name}</Link> — {poste.brand}{' '}
            {poste.model}
          </p>
        </div>
        <Link className="bouton" to={`/postes/${poste.id}`}>
          Retour à la fiche
        </Link>
      </div>

      {error !== null && (
        <Notice tone="erreur" title="Import refusé">
          {error}
          {duplicate !== null && (
            <div style={{ marginTop: 'var(--espace-3)' }}>
              Doublon du diagnostic{' '}
              <Link to={`/diagnostics/${duplicate.diagnosticId}`}>
                {diagnosticTypeLabel(duplicate.type)} du{' '}
                {formatDateTime(duplicate.performedAt)}
              </Link>{' '}
              (fichier {duplicate.originalFilename}).
            </div>
          )}
        </Notice>
      )}

      {step.kind === 'choix' && (
        <form className="panneau" onSubmit={onUpload}>
          <div className="panneau__entete">
            <span className="panneau__titre">Poste et fichier</span>
          </div>
          <div className="panneau__corps pile pile--serree">
            <Notice tone="info">
              Le poste n’est pas modifiable ici : il vient de la fiche d’où vous
              arrivez. C’est voulu — les rapports UserDiag ne portent aucun
              identifiant de machine, deux postes identiques produisent des
              rapports indiscernables, et le rattachement ne peut donc venir que
              du contexte.
            </Notice>

            <div className="grille-champs">
              <Field label="Fichier du rapport" hint="PDF, HTML ou TXT." wide>
                <input
                  type="file"
                  accept={ACCEPTED_UPLOAD_ACCEPT}
                  required
                  onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                />
              </Field>
            </div>

            <div className="ligne">
              <button
                type="submit"
                className="bouton bouton--primaire pousse"
                disabled={busy || file === null}
              >
                {busy ? 'Envoi…' : 'Envoyer le fichier'}
              </button>
            </div>
          </div>
        </form>
      )}

      {step.kind === 'confirmation' && form !== null && (
        <form className="pile" onSubmit={onConfirm}>
          <section className="panneau">
            <div className="panneau__entete">
              <span className="panneau__titre">Fichier reçu</span>
            </div>
            <div className="panneau__corps pile pile--serree">
              <dl className="definitions">
                <dt>Nom d'origine</dt>
                <dd>{step.staging.originalFilename}</dd>
                <dt>Format</dt>
                <dd>{fileFormatLabel(step.staging.fileFormat)}</dd>
                <dt>Taille</dt>
                <dd>{formatSize(step.staging.sizeBytes)}</dd>
                <dt>Empreinte SHA-256</dt>
                <dd className="mono">{step.staging.sha256}</dd>
              </dl>
              <ExtractionNotice extraction={step.extraction} />
            </div>
          </section>

          {step.proposal !== null && (
            <InventoryProposalPanel
              proposal={step.proposal}
              checked={checked}
              disabled={busy}
              onToggle={(index) =>
                setChecked((current) => {
                  const next = new Set(current);
                  if (!next.delete(index)) next.add(index);
                  return next;
                })
              }
              onToggleAll={(indexes) => setChecked(new Set(indexes))}
            />
          )}

          <section className="panneau">
            <div className="panneau__entete">
              <span className="panneau__titre">Valeurs à confirmer</span>
            </div>
            <div className="panneau__corps">
              <div className="grille-champs">
                <Field label="Type de diagnostic">
                  <select
                    value={form.type}
                    onChange={(event) =>
                      patch({ type: event.target.value as DiagnosticType })
                    }
                  >
                    {DIAGNOSTIC_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {diagnosticTypeLabel(type)}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Date du diagnostic">
                  <input
                    type="date"
                    value={form.performedDate}
                    required
                    onChange={(event) => patch({ performedDate: event.target.value })}
                  />
                </Field>

                <Field
                  label="Heure du diagnostic"
                  hint={
                    hourMissing(step.extraction)
                      ? "Le rapport ne donne pas l'heure : saisissez-la."
                      : undefined
                  }
                >
                  <input
                    type="time"
                    value={form.performedTime}
                    required
                    onChange={(event) => patch({ performedTime: event.target.value })}
                  />
                </Field>

                <Field
                  label="Rapport en ligne"
                  hint="Adresse sur userdiag.com, si elle est connue."
                >
                  <input
                    type="url"
                    value={form.reportUrl}
                    placeholder="https://userdiag.com/…"
                    onChange={(event) => patch({ reportUrl: event.target.value })}
                  />
                </Field>

                <Field label="Processeur">
                  <input
                    value={form.processeur}
                    maxLength={300}
                    onChange={(event) => patch({ processeur: event.target.value })}
                  />
                </Field>

                <Field label="Carte graphique">
                  <input
                    value={form.carteGraphique}
                    maxLength={300}
                    onChange={(event) => patch({ carteGraphique: event.target.value })}
                  />
                </Field>

                <Field label="Mémoire">
                  <input
                    value={form.memoire}
                    maxLength={300}
                    onChange={(event) => patch({ memoire: event.target.value })}
                  />
                </Field>

                <Field label="Disques" hint="Un disque par ligne." wide>
                  <textarea
                    value={form.disques}
                    onChange={(event) => patch({ disques: event.target.value })}
                  />
                </Field>

                <Field label="Motif de l'import" wide hint="Texte libre, visible sur la frise.">
                  <textarea
                    value={form.reason}
                    maxLength={2000}
                    onChange={(event) => patch({ reason: event.target.value })}
                  />
                </Field>
              </div>
            </div>
            <div className="pagination">
              <button
                type="button"
                className="bouton bouton--danger"
                onClick={onAbandon}
                disabled={busy}
              >
                Abandonner ce fichier
              </button>
              <button
                type="submit"
                className="bouton bouton--primaire pousse"
                disabled={busy}
              >
                {busy ? 'Enregistrement…' : "Enregistrer l'import"}
              </button>
            </div>
          </section>
        </form>
      )}

    </div>
  );
}
